import { useMutation } from "@apollo/client";
import _ from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalConfigContext } from "../../../../context/GlobalConfigContext";
import updateProgramConfiguration from "../../../../graphql/mutations/updateProgramConfiguration";

/**
 * Hook for managing configuration form state within drawers.
 * Handles loading config data, tracking changes, validation, and saving.
 *
 * @param {string} configKey - The configuration key (e.g., 'partner_configuration')
 * @param {Object} fieldDefinitions - Field definitions object with validation and metadata
 * @param {boolean} isOpen - Whether the drawer is currently open
 * @returns {Object} Form state and handlers
 */
export default function useDrawerConfigForm(
  configKey,
  fieldDefinitions = {},
  isOpen = false
) {
  const {
    programConfigurations,
    updateProgramConfigurations,
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);

  const [updateProgramConfigurationMutation] = useMutation(
    updateProgramConfiguration
  );

  const [formData, setFormData] = useState({});
  const [initialFormData, setInitialFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  // Load configuration data when drawer opens or programConfigurations change
  useEffect(() => {
    if (!isOpen || !configKey) return;

    const configData = _.find(programConfigurations, { key: configKey });
    if (configData?.value?.data) {
      const data = JSON.parse(JSON.stringify(configData.value.data));
      setInitialFormData(data);
      setFormData(data);
      setFieldErrors({});
    }
  }, [programConfigurations, configKey, isOpen]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setFieldErrors({});
      setConfirmationModalOpen(false);
      setDiscardModalOpen(false);
    }
  }, [isOpen]);

  const setDeepValue = (obj, path, value) => {
    const keys = path
      .replace(/\[(\w+)\]/g, ".$1")
      .replace(/^\./, "")
      .split(".");

    const newObj = { ...obj };
    let current = newObj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== "object") {
        const nextKey = keys[i + 1];
        current[key] = /^\d+$/.test(nextKey) ? [] : {};
      }
      current[key] = Array.isArray(current[key])
        ? [...current[key]]
        : { ...current[key] };
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return newObj;
  };

  const getDeepValue = (obj, path) => {
    const keys = path
      .replace(/\[(\w+)\]/g, ".$1")
      .replace(/^\./, "")
      .split(".");
    return keys.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
  };

  const isUpdated = useMemo(() => {
    return JSON.stringify(initialFormData) !== JSON.stringify(formData);
  }, [initialFormData, formData]);

  const hasFieldErrors = useMemo(() => {
    const hasAnyNestedError = (obj) => {
      if (typeof obj !== "object" || obj === null) {
        return obj !== null && obj !== false && obj !== "" && obj !== undefined;
      }
      return Object.values(obj).some((value) => hasAnyNestedError(value));
    };

    const hasAnyError = hasAnyNestedError(fieldErrors);

    const hasEmptyRequiredField = Object.entries(fieldDefinitions).some(
      ([path, fieldDef]) => {
        if (!fieldDef?.required) return false;
        const value = getDeepValue(formData, path);
        return !value?.toString().trim?.();
      }
    );

    return hasAnyError || hasEmptyRequiredField;
  }, [formData, fieldErrors, fieldDefinitions]);

  const validateField = (name, value) => {
    const fieldDef = getDeepValue(fieldDefinitions, name);

    if (fieldDef?.validate) {
      return fieldDef.validate(value);
    }

    if (fieldDef?.required && (!value || value.toString().trim?.() === "")) {
      return "This field is required";
    }

    return false;
  };

  const handleFieldChange = (path, value) => {
    const stringValue = typeof value === "number" ? value.toString() : value;
    setFormData((prev) => setDeepValue(prev, path, stringValue));

    const error = validateField(path, stringValue);
    setFieldErrors((prev) => setDeepValue(prev, path, error));
  };

  const handleToggleChange = (path, checked) => {
    setFormData((prev) => setDeepValue(prev, path, checked));
  };

  const handleSaveConfirmation = async () => {
    const params = {
      variables: {
        input: {
          slug: configKey,
          data: JSON.stringify(formData),
        },
        id: programConfig?.id,
      },
    };

    try {
      setIsLoading(true);
      const res = await updateProgramConfigurationMutation(params);
      if (res?.data?.updateProgramConfiguration) {
        updateProgramConfigurations(
          res?.data?.updateProgramConfiguration?.configurations
        );
        setInitialFormData(JSON.parse(JSON.stringify(formData)));
        setToast({
          open: true,
          type: "success",
          title: "Settings updated successfully!",
          description: `You have just saved the ${configKey.replace(/_/g, " ")}`,
        });
        setIsLoading(false);
      }
    } catch {
      setToast({
        open: true,
        type: "error",
        title: "Something went wrong!",
        description: "Please try again",
      });
      setIsLoading(false);
    }

    setConfirmationModalOpen(false);
  };

  const handleDiscard = () => {
    setFormData(JSON.parse(JSON.stringify(initialFormData)));
    setFieldErrors({});
    setDiscardModalOpen(false);
  };

  return {
    formData,
    initialFormData,
    fieldErrors,
    isUpdated,
    isLoading,
    hasFieldErrors,
    confirmationModalOpen,
    discardModalOpen,
    toast,
    setFormData,
    setToast,
    setConfirmationModalOpen,
    setDiscardModalOpen,
    handleFieldChange,
    handleToggleChange,
    handleSaveConfirmation,
    handleDiscard,
    getDeepValue,
  };
}
